import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "../api/client";

export type NotificationDigestCadence = "off" | "daily" | "weekly";

export interface PerEventToggles {
  decision_needed: boolean;
  replan_ready: boolean;
  agent_failure: boolean;
  kr_target_hit: boolean;
}

export interface NotificationPrefs {
  digest_cadence: NotificationDigestCadence;
  per_event: PerEventToggles;
}

interface PrefsResponse {
  prefs: NotificationPrefs & {
    id: string;
    user_id: string;
    workspace_id: string | null;
    created_at: string;
    updated_at: string;
  };
}

/**
 * Read the user's effective notification preferences. Pass workspaceId=null
 * (or omit) for the global defaults; pass a workspace id to get the
 * workspace-specific override (with global fallback resolved server-side).
 */
export function useNotificationPrefs(workspaceId?: string | null) {
  const qs = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : "";
  return useQuery<PrefsResponse, Error>({
    queryKey: ["notification-prefs", workspaceId ?? "global"],
    queryFn: () => apiGet<PrefsResponse>(`/api/me/notification-prefs${qs}`),
    staleTime: 60_000,
  });
}

/**
 * Update notification prefs. workspace_id null/omitted = update global.
 * per_event is sparse-merged with defaults server-side, so callers can
 * send only the toggles they want to flip.
 */
export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation<
    PrefsResponse,
    Error,
    {
      digest_cadence: NotificationDigestCadence;
      per_event: Partial<PerEventToggles>;
      workspace_id?: string | null;
    }
  >({
    mutationFn: (body) => apiPut<PrefsResponse>("/api/me/notification-prefs", body),
    onSuccess: (data, vars) => {
      qc.invalidateQueries({
        queryKey: ["notification-prefs", vars.workspace_id ?? "global"],
      });
    },
  });
}
